import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { getAuthToken } from "../../utils/auth";
import { handleApiError } from "../handleApiError";
import i18n from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL;
const authHeader = () => ({
  headers: { Authorization: `${getAuthToken()}` },
});

// ========================
// PUBLIC
// ========================
export const getActiveCompanies = createAsyncThunk(
  "company/active",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/company/active`, {
        params: { page, limit },
      });
      return {
        data: res.data.data || [],
        responseDetails: res.data.responseDetails || {},
      };
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const getVerifiedCompanies = createAsyncThunk(
  "company/verified",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/company/verified`);
      return { data: res.data.data || [] };
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const getCompanyBySlug = createAsyncThunk(
  "company/slug",
  async (slug, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/company/slug/${slug}`);
      // console.log(res);
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const getCompanyById = createAsyncThunk(
  "company/getOne",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/company/${id}`);
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const getCompanyStats = createAsyncThunk(
  "company/stats",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/company/${id}/stats`);
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

// ========================
// USER (OWNER)
// ========================
export const getMyCompany = createAsyncThunk(
  "company/myCompany",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/company/myCompany`, authHeader());
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

// ========================
// ADMIN
// ========================
export const getAllCompaniesAdmin = createAsyncThunk(
  "company/admin/all",
  async ({ page = 1, limit = 20, search } = {}, { rejectWithValue }) => {
    try {
      const params = {
        page,
        limit,
        ...(search && { search }),
      };
      const res = await axios.get(`${API_URL}/company`, {
        params,
        ...authHeader(),
      });
      return {
        data: res.data.data || [],
        responseDetails: res.data.responseDetails || {},
      };
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const addCompany = createAsyncThunk(
  "company/add",
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${API_URL}/company`, data, authHeader());
      toast.success(i18n.t("common.companyAdded"));
      return res.data.data;
    } catch (e) {
      handleApiError(e);
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const updateCompany = createAsyncThunk(
  "company/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${API_URL}/company/${id}`,
        data,
        authHeader(),
      );
      toast.success(i18n.t("common.companyUpdated"));
      return res.data.data;
    } catch (e) {
      handleApiError(e);
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const updateCompanyContact = createAsyncThunk(
  "company/updateContact",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${API_URL}/company/${id}/contact`,
        data,
        authHeader(),
      );
      toast.success(i18n.t("common.contactUpdated"));
      return res.data.data;
    } catch (e) {
      handleApiError(e);
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const toggleCompanyActive = createAsyncThunk(
  "company/toggleActive",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.patch(
        `${API_URL}/company/${id}/toggle-active`,
        {},
        authHeader(),
      );
      return id;
    } catch (e) {
      handleApiError(e);
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const toggleCompanyVerification = createAsyncThunk(
  "company/toggleVerification",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.patch(
        `${API_URL}/company/${id}/toggle-verification`,
        {},
        authHeader(),
      );
      return id;
    } catch (e) {
      handleApiError(e);
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);

export const deleteCompany = createAsyncThunk(
  "company/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/company/${id}`, authHeader());
      toast.success(i18n.t("common.companyDeleted"));
      return id;
    } catch (e) {
      handleApiError(e);
      return rejectWithValue(e.response?.data || e.message);
    }
  },
);
